import { createContext, ReactNode, useState, useEffect } from "react";
import Cookies from "js-cookie";
import axios from "axios";

interface User {
  id: string;
  email: string;
  token: string;
  role: string;
}
interface AuthContextData {
  loginUser: any;
  logoutUser: any;
  user: User | any;
  setUser: void | any;
  loading: boolean;
}

interface LoginData {
  email: string;
  password: string;
}

const API_URL = "/api/v1";

export const AuthContext = createContext<AuthContextData>({
  loginUser: () => {},
  logoutUser: () => {},
  user: null,
  setUser: () => {},
  loading: true
});

type AuthContextProviderProps = {
  children: ReactNode;
};

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const [user, setUser] = useState<User | any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const manageUserSession = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/auth/session`);
        if (res.status === 200) {
          if(res.data.success === true){
            setUser(res.data.user);
          }else if(res.data.success === false){
            setUser(null);
          }
        }
      } catch (error: any) {
        // Handle error without logging
        setUser(null)
        if (error.response?.status === 401) {
          console.log("Login to continue");
        }
        // Suppress all logs
      }
      finally{
        setLoading(false);
      }
    };
    manageUserSession();
    // eslint-disable-next-line
  }, []);

  const loginUser = async (logindata: LoginData) => {
    try {
      const res = await axios.post(`${API_URL}/auth/login`, logindata);
      if (res.status === 200 && res.data.success === true) {
        setUser({
          id: res.data.user._id,
          email: res.data.user.email,
          role: res.data.user.role,
          token: res.data.token,
        });

        return {
          success: true,
          access: true,
          message: "Login Success",
          description: "User logged in successfully.",
        };
      }
    } catch (error: any) {
      if (error.status === 400 && error.response.data.path === "email-invite") {
        Cookies.remove("ivtoken");
      }
      let errordata = {
        success: false,
        access: false,
        status: error.response.data.status,
        error: error.response.data.error,
        message: error.response.data.message,
      };
      return errordata;
    }
  };

  const logoutUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/logout`);
      if (res.status === 200 && res.data.success === true) {
        setUser(null);
        return {
          status: 200,
          success: true,
          user: null,
        };
      }
    } catch (error: any) {
      console.log("Error user logging out:", error);
    }
  };

  const contextData = {
    loginUser,
    logoutUser,
    user,
    setUser,
    loading
  };
  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
